import React from 'react';
import PropTypes from 'prop-types';
import { graphql } from 'gatsby';
import Layout from '@components/layout';
import BlockBuilder from '@repo/ui-components/sanity/BlockBuilder';
import Button from '@repo/ui-components/button/Button';
import Hero from '@repo/ui-components/hero/Hero';
import PageWrapper from '@repo/ui-components/page-wrapper/PageWrapper';
import theme from '@components/theme';
import getInternalLink from '@components/utils/getInternalLink';
import createOpenGraphTags from '@repo/utils/createOpenGraphTags';
import { getLocalizedPagePath } from '@repo/gatsby-localization-helpers';

function SanityPage({ data, pageContext }) {
  const {
    SEOMetadata,
    title,
    description,
    image,
    blockBuilder,
    includePageBackground,
    hero,
    slug,
  } = data?.sanityPage ?? {};
  const { locale, linkHreflangValues } = pageContext;

  return (
    <Layout
      headTagData={{
        title: SEOMetadata?.title || title,
        description: SEOMetadata?.description || description,
        canonicalUrl: SEOMetadata?.canonicalUrl,
        noIndex: SEOMetadata?.noIndex,
        noFollow: SEOMetadata?.noFollow,
        meta: createOpenGraphTags(
          {
            title: SEOMetadata?.ogTitle || SEOMetadata?.title || title,
            description:
              SEOMetadata?.ogDescription ||
              SEOMetadata?.description ||
              description,
            url:
              SEOMetadata?.ogUrl ||
              SEOMetadata?.canonicalUrl ||
              (typeof window !== 'undefined' && window?.location?.pathname) ||
              getLocalizedPagePath('/', locale),
            image: SEOMetadata?.ogImage || image,
          },
          'sportdog'
        ),
      }}
      fullWidth
      linkHreflangValues={linkHreflangValues}
    >
      <PageWrapper
        background={includePageBackground ? theme.colors.background : 'inherit'}
        padding={
          slug?.current === 'home-page'
            ? '0 1.625rem 1.625rem 1.625rem'
            : '1.625rem'
        }
      >
        {hero && <Hero hero={hero} getInternalLink={getInternalLink} />}

        {blockBuilder?.blocks && (
          <BlockBuilder
            blocks={blockBuilder?.blocks}
            getInternalLink={getInternalLink}
            Button={Button}
          />
        )}
      </PageWrapper>
    </Layout>
  );
}

export const query = graphql`
  query SanityPageQuery($_id: String!) {
    sanityPage(_id: { eq: $_id }, siteIdentifier: { eq: "sportdog" }) {
      SEOMetadata {
        title
        description
        canonicalUrl
        noIndex
        noFollow
        ogTitle
        ogDescription
        ogUrl
        ogImage {
          asset {
            url
          }
        }
      }
      slug {
        current
      }
      title
      image: _rawImage(resolveReferences: { maxDepth: 3 })
      description
      blockBuilder: _rawLocalizedBlockBuilder(
        resolveReferences: { maxDepth: 13 }
      )
      hero {
        presentation
        heroType
        compactImage
        mediaItem {
          asset {
            _id
          }
          crop {
            bottom
            left
            right
            top
          }
          hotspot {
            x
            y
          }
          alt
        }
        externalVideo
        internalVideo {
          video: asset {
            url
          }
        }
        heroPoster {
          asset {
            url
          }
        }
        headline1
        headline2
        headlineStyle
        subheadType
        portableTextHeadline: _rawPortableTextHeadline(
          resolveReferences: { maxDepth: 2 }
        )
        subheadlineStyle
        textAlignment
        enableTextShadow
        enableCta
        cta {
          linkType
          internalLink: _rawLocalizedInternalLink(
            resolveReferences: { maxDepth: 2 }
          )
          caption
          href
        }
        themeStyle
      }
      includePageBackground
    }
  }
`;

SanityPage.propTypes = {
  pageContext: PropTypes.shape({
    locale: PropTypes.string,
    linkHreflangValues: PropTypes.arrayOf(PropTypes.shape({})),
  }).isRequired,
  data: PropTypes.shape({
    sanityPage: PropTypes.shape({
      slug: PropTypes.shape({ current: PropTypes.string }),
      hero: PropTypes.shape({
        presentation: PropTypes.string,
        headline1: PropTypes.string,
        headline2: PropTypes.string,
        body: PropTypes.arrayOf(PropTypes.shape({})),
        textAlignment: PropTypes.string,
        mediaItem: PropTypes.shape({
          asset: PropTypes.shape({
            alt: PropTypes.string,
          }),
        }),
        enableTextShadow: PropTypes.bool,
        enableCta: PropTypes.bool,
        cta: PropTypes.shape({}),
        themeStyle: PropTypes.string,
      }),
      blockBuilder: PropTypes.shape({
        blocks: PropTypes.arrayOf(PropTypes.shape({})),
      }),
      SEOMetadata: PropTypes.shape({
        title: PropTypes.string,
        description: PropTypes.string,
        canonicalUrl: PropTypes.string,
        noIndex: PropTypes.bool,
        noFollow: PropTypes.bool,
        ogTitle: PropTypes.string,
        ogDescription: PropTypes.string,
        ogUrl: PropTypes.string,
        ogImage: PropTypes.shape({
          asset: PropTypes.shape({
            url: PropTypes.string,
          }),
        }),
      }),
      title: PropTypes.string,
      description: PropTypes.string,
      image: PropTypes.shape({
        asset: PropTypes.shape({
          url: PropTypes.string,
        }),
        alt: PropTypes.string,
      }),
      body: PropTypes.arrayOf({}),
      includePageBackground: PropTypes.bool,
    }),
  }).isRequired,
};

export default SanityPage;
